import React, { useEffect, useMemo, useState } from "react";
import { lessonAPI } from "../services/api";
import "./LessonLibraryPage.css";

const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

const toSearchText = (value) => String(value || "").trim().toLowerCase();

const uniqueCodes = (codes = []) => {
  const seen = new Set();
  return codes.filter((code) => {
    const key = String(code || "").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getAlignedCodes = (lesson) => {
  const standardsCodes = Array.isArray(lesson?.standards)
    ? lesson.standards.map((item) => item?.teks_code).filter(Boolean)
    : [];

  return uniqueCodes([lesson?.primary_teks_code, ...standardsCodes].filter(Boolean));
};

const lessonSort = (a, b) => {
  const chapterDelta = collator.compare(String(a?.chapter_ref || ""), String(b?.chapter_ref || ""));
  if (chapterDelta !== 0) return chapterDelta;
  return collator.compare(String(a?.lesson_title || ""), String(b?.lesson_title || ""));
};

const unitSort = (a, b) => Number(a.unitNumber) - Number(b.unitNumber);

const lessonMatchesQuery = (lesson, rawQuery) => {
  const query = toSearchText(rawQuery);
  if (!query) return true;

  const codes = getAlignedCodes(lesson).join(" ");
  const haystack = [
    lesson?.lesson_title,
    lesson?.chapter_ref,
    lesson?.primary_teks_code,
    lesson?.primary_teks_description,
    codes,
  ]
    .map(toSearchText)
    .join(" ");

  return haystack.includes(query);
};

const LessonLibraryPage = ({ courseId, onOpenLesson }) => {
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!courseId) {
      setLessons([]);
      setErrorMessage("");
      setQuery("");
      return;
    }

    let cancelled = false;
    const loadLessons = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await lessonAPI.getAll({ courseId });
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        setLessons(rows);
      } catch (error) {
        console.error("Error loading lessons library:", error);
        if (!cancelled) {
          setLessons([]);
          setErrorMessage("Could not load lessons for this course right now.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadLessons();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const filteredLessons = useMemo(
    () => lessons.filter((lesson) => lessonMatchesQuery(lesson, query)),
    [lessons, query],
  );

  const groupedByUnit = useMemo(() => {
    const byUnit = new Map();

    filteredLessons.forEach((lesson) => {
      const unitNumber = Number(lesson?.unit_number || 0);
      if (!Number.isFinite(unitNumber) || unitNumber <= 0) return;

      if (!byUnit.has(unitNumber)) {
        byUnit.set(unitNumber, []);
      }
      byUnit.get(unitNumber).push(lesson);
    });

    return Array.from(byUnit.entries())
      .map(([unitNumber, unitLessons]) => ({
        unitNumber,
        lessons: [...unitLessons].sort(lessonSort),
      }))
      .sort(unitSort);
  }, [filteredLessons]);

  const totalUnits = useMemo(() => new Set(lessons.map((row) => row.unit_number)).size, [lessons]);
  const totalLessonCount = lessons.length;
  const visibleLessonCount = filteredLessons.length;

  if (!courseId) {
    return (
      <div className="panel-card lessons-library-page">
        <h2>Lessons</h2>
        <p>Select a course to browse lessons by unit.</p>
      </div>
    );
  }

  return (
    <div className="panel-card lessons-library-page" data-tour="lessons-library-root">
      <div className="lessons-library-header">
        <div>
          <h2>Lessons by Unit</h2>
          <p>Find any lesson quickly, then open it directly.</p>
        </div>
        <input
          type="search"
          className="lessons-library-search"
          placeholder="Search by lesson title, chapter, or TEKS code"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search lessons"
        />
      </div>

      <div className="lessons-library-kpis">
        <div className="lessons-kpi">
          <span className="label">Units</span>
          <strong>{totalUnits}</strong>
        </div>
        <div className="lessons-kpi">
          <span className="label">Lessons</span>
          <strong>{totalLessonCount}</strong>
        </div>
        <div className="lessons-kpi">
          <span className="label">Visible</span>
          <strong>{visibleLessonCount}</strong>
        </div>
      </div>

      {isLoading && <p>Loading lessons...</p>}
      {!isLoading && errorMessage && <p className="lessons-error">{errorMessage}</p>}
      {!isLoading && !errorMessage && groupedByUnit.length === 0 && (
        <p>No lessons matched this search.</p>
      )}

      {!isLoading && !errorMessage && groupedByUnit.length > 0 && (
        <div className="lessons-units-list">
          {groupedByUnit.map((unitGroup) => (
            <section key={unitGroup.unitNumber} className="lessons-unit-card">
              <header className="lessons-unit-header">
                <h3>Unit {unitGroup.unitNumber}</h3>
                <span>{unitGroup.lessons.length} lessons</span>
              </header>

              <div className="lessons-list">
                {unitGroup.lessons.map((lesson) => {
                  const alignedCodes = getAlignedCodes(lesson);
                  return (
                    <article key={lesson.id} className="lesson-row-card">
                      <div className="lesson-row-copy">
                        <h4>{lesson.lesson_title}</h4>
                        <p className="lesson-row-meta">
                          {lesson.chapter_ref || "Chapter reference not set"}
                        </p>
                        {alignedCodes.length > 0 && (
                          <div className="lesson-row-tags">
                            {alignedCodes.map((code) => (
                              <span key={`${lesson.id}-${code}`} className="lesson-teks-chip">
                                {code}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => onOpenLesson?.(lesson.id)}
                      >
                        Open lesson
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default LessonLibraryPage;
